import { Trash2, Plus, AlertCircle } from 'lucide-react';
import { DayWeekContainer } from './DayWeekContainer';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui';
import { generateTimeSlots, TimeSlot } from '../utils/generateTimeSlots';
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

export interface TimeSlotPair {
	start: TimeSlot | null;
	end: TimeSlot | null;
}

export interface CalendarDataProps {
	selectedDayWeeks: number[];
	slots: TimeSlotPair[];
}

interface DefaultData {
	selectedDayWeek: number;
	selectedInitalTime: string;
	selectedFinalTime: string;
}

interface CalendarContainerProps {
	onAction: (data: CalendarDataProps) => void;
	isSuccess?: boolean;
	defaultData?: DefaultData;
	onCancel: () => void;
	isEditMode?: boolean;
}

const timeToMinutes = (time: string) => {
	const [hours, minutes] = time.split(':').map(Number);
	return hours * 60 + minutes;
};

const CreateCalendarContainer = ({
	onAction,
	isSuccess,
	defaultData,
	onCancel,
	isEditMode = false,
}: CalendarContainerProps) => {
	const initialSlots = generateTimeSlots();
	const finalSlots = generateTimeSlots();
	const defaultTime = initialSlots.length > 0 ? initialSlots[0] : null;

	const [selectedDayWeeks, setSelectedDayWeeks] = useState<number[]>([]);
	const [timeSlots, setTimeSlots] = useState<TimeSlotPair[]>([
		{ start: defaultTime, end: defaultTime },
	]);

	const [conflictError, setConflictError] = useState<string | null>(null);
	const [touched, setTouched] = useState(false);

	const handleOnSelectDayWeek = (value: number) => {
		if (isEditMode) {
			setSelectedDayWeeks([value]);
			return;
		}

		setSelectedDayWeeks((prev) =>
			prev.includes(value)
				? prev.filter((day) => day !== value)
				: [...prev, value],
		);
	};

	const handleSelectAll = () => {
		if (isEditMode) return;

		setSelectedDayWeeks(
			selectedDayWeeks.length === 7 ? [] : [0, 1, 2, 3, 4, 5, 6],
		);
	};

	const addTimeSlot = () => {
		if (isEditMode) return;

		setTouched(true);
		setTimeSlots([...timeSlots, { start: defaultTime, end: defaultTime }]);
	};

	const removeTimeSlot = (index: number) => {
		setTouched(true);
		if (timeSlots.length > 1) {
			setTimeSlots(timeSlots.filter((_, i) => i !== index));
		} else {
			updateTimeSlot(index, 'start', defaultTime);
			updateTimeSlot(index, 'end', defaultTime);
		}
	};

	const updateTimeSlot = (
		index: number,
		field: 'start' | 'end',
		value: TimeSlot | null,
	) => {
		setTouched(true);
		const newSlots = [...timeSlots];
		newSlots[index] = { ...newSlots[index], [field]: value };
		setTimeSlots(newSlots);
	};

	useEffect(() => {
		const validSlots = timeSlots.filter((s) => s.start && s.end);
		const hasInvalidRange = validSlots.some(
			(s) => timeToMinutes(s.end!.label) <= timeToMinutes(s.start!.label),
		);

		if (hasInvalidRange) {
			setConflictError('O horário final deve ser maior que o inicial.');
			return;
		}

		for (let i = 0; i < validSlots.length; i++) {
			for (let j = i + 1; j < validSlots.length; j++) {
				const startA = timeToMinutes(validSlots[i].start!.label);
				const endA = timeToMinutes(validSlots[i].end!.label);
				const startB = timeToMinutes(validSlots[j].start!.label);
				const endB = timeToMinutes(validSlots[j].end!.label);
				if (startA < endB && endA > startB) {
					setConflictError(
						'Existem horários duplicados ou sobrepostos.',
					);
					return;
				}
			}
		}
		setConflictError(null);
	}, [timeSlots]);

	useEffect(() => {
		if (isSuccess) {
			setTimeSlots([{ start: defaultTime, end: defaultTime }]);
			setSelectedDayWeeks([]);
			setTouched(false);
		}
	}, [isSuccess]);

	useEffect(() => {
		if (defaultData) {
			const start = initialSlots.find(
				(s) => s.value === defaultData.selectedInitalTime,
			);
			const end = finalSlots.find(
				(s) => s.value === defaultData.selectedFinalTime,
			);
			if (defaultData.selectedDayWeek !== undefined)
				setSelectedDayWeeks([defaultData.selectedDayWeek]);
			if (start && end) setTimeSlots([{ start, end }]);
		}
	}, [defaultData]);

	const isValid =
		selectedDayWeeks.length > 0 &&
		timeSlots.every((s) => s.start && s.end) &&
		!conflictError;
	const showErrorMessage = conflictError && touched;

	return (
		<div
			className={cn(
				'space-y-8 rounded-3xl bg-white p-6 md:p-8',
				isEditMode
					? 'border border-stone-200'
					: 'border border-stone-200 shadow-sm',
			)}
		>
			<div className="space-y-4">
				<div className="flex items-center justify-between">
					<h4 className="text-lg font-bold text-stone-900">
						1. Selecione os dias da semana
					</h4>
					{!isEditMode && (
						<button
							onClick={handleSelectAll}
							className="text-sm font-semibold text-stone-900 hover:underline"
						>
							{selectedDayWeeks.length === 7
								? 'Deselecionar todos'
								: 'Selecionar todos'}
						</button>
					)}
				</div>
				<div className="flex flex-wrap gap-2">
					{Array.from({ length: 7 }).map((_, index) => (
						<DayWeekContainer
							key={index}
							value={index}
							onSelect={handleOnSelectDayWeek}
							isSelected={selectedDayWeeks.includes(index)}
						/>
					))}
				</div>
			</div>

			<div className="space-y-6">
				<div className="flex items-center justify-between">
					<h4 className="text-lg font-bold text-stone-900">
						2. Defina os horários
					</h4>
					<span className="hidden text-xs italic text-stone-400 md:block">
						Esses horários serão aplicados a todos os dias
						selecionados.
					</span>
				</div>

				<div className="space-y-4">
					{timeSlots.map((slot, index) => (
						<div
							key={index}
							className={cn(
								'group flex flex-col items-center gap-4 rounded-2xl px-3 py-2 transition-colors duration-200 md:flex-row',
								showErrorMessage
									? 'border border-red-100 bg-red-50'
									: 'border border-transparent bg-stone-50 hover:bg-stone-100',
							)}
						>
							<div className="w-full flex-1">
								<label className="mb-1 block text-[10px] font-bold uppercase text-stone-400">
									Início
								</label>
								<Select
									value={
										slot.start
											? JSON.stringify(slot.start)
											: ''
									}
									onValueChange={(val) =>
										updateTimeSlot(
											index,
											'start',
											JSON.parse(val),
										)
									}
								>
									<SelectTrigger className="h-9 w-full rounded-xl border-none bg-white px-4 text-left shadow-sm focus:ring-2 focus:ring-stone-950/10">
										<SelectValue>
											{slot.start?.label}
										</SelectValue>
									</SelectTrigger>
									<SelectContent className="h-60 min-w-[85px]">
										<SelectGroup>
											{initialSlots.map((time, i) => (
												<SelectItem
													key={i}
													value={JSON.stringify(time)}
													className="cursor-pointer justify-center"
												>
													{time.label}
												</SelectItem>
											))}
										</SelectGroup>
									</SelectContent>
								</Select>
							</div>
							<span className="pt-4 text-sm font-bold text-stone-300">
								até
							</span>
							<div className="w-full flex-1">
								<label className="mb-1 block text-[10px] font-bold uppercase text-stone-400">
									Fim
								</label>
								<Select
									value={
										slot.end ? JSON.stringify(slot.end) : ''
									}
									onValueChange={(val) =>
										updateTimeSlot(
											index,
											'end',
											JSON.parse(val),
										)
									}
								>
									<SelectTrigger className="h-9 w-full rounded-xl border-none bg-white px-4 text-left shadow-sm focus:ring-2 focus:ring-stone-950/10">
										<SelectValue>
											{slot.end?.label}
										</SelectValue>
									</SelectTrigger>
									<SelectContent className="h-60 min-w-[85px]">
										<SelectGroup>
											{finalSlots.map((time, i) => (
												<SelectItem
													key={i}
													value={JSON.stringify(time)}
													className="cursor-pointer justify-center"
												>
													{time.label}
												</SelectItem>
											))}
										</SelectGroup>
									</SelectContent>
								</Select>
							</div>
							<button
								onClick={() => removeTimeSlot(index)}
								className="mt-4 rounded-xl p-2 text-stone-400 transition-colors hover:bg-white hover:text-red-500 hover:shadow-sm"
							>
								<Trash2 className="size-5" />
							</button>
						</div>
					))}
				</div>

				{!isEditMode && (
					<button
						onClick={addTimeSlot}
						className="flex w-full items-center justify-center space-x-2 rounded-2xl border-2 border-dashed border-stone-200 py-4 font-semibold text-stone-400 transition-all hover:border-stone-900/30 hover:text-stone-900"
					>
						<Plus size={20} />
						<span>Adicionar outro intervalo de tempo</span>
					</button>
				)}
			</div>

			{showErrorMessage ? (
				<div className="animate-in slide-in-from-top-2 flex items-center gap-3 rounded-xl border border-red-100 bg-red-50 p-3 text-red-600">
					<AlertCircle className="size-5 shrink-0" />
					<span className="text-sm font-semibold">
						{conflictError}
					</span>
				</div>
			) : (
				<div className="flex items-start gap-3 rounded-xl border border-stone-200 bg-stone-50 p-4">
					<div className="mt-0.5 flex h-5 w-5 min-w-[20px] items-center justify-center rounded-full border-2 border-stone-900 text-xs font-bold text-stone-900">
						i
					</div>
					<p className="text-sm leading-tight text-stone-600">
						Ao salvar, você criará{' '}
						<span className="font-bold text-stone-900">
							{selectedDayWeeks.length * timeSlots.length}
						</span>{' '}
						slots de horário distribuídos pelos dias selecionados.
					</p>
				</div>
			)}

			<div className="flex w-full gap-4 pt-4">
				<button
					type="button"
					onClick={onCancel}
					className="flex-1 rounded-2xl border border-stone-200 px-6 py-4 text-center font-bold text-stone-600 transition-colors hover:bg-stone-50"
				>
					Voltar
				</button>
				<button
					type="button"
					onClick={() =>
						onAction({ selectedDayWeeks, slots: timeSlots })
					}
					disabled={!isValid}
					className={cn(
						'flex-[2] transform rounded-2xl px-6 py-4 text-center font-bold text-white shadow-lg transition-all active:scale-[0.98]',
						!isValid
							? 'cursor-not-allowed bg-stone-300 shadow-none'
							: 'bg-stone-950 shadow-stone-200 hover:bg-stone-900',
					)}
				>
					{defaultData
						? 'Confirmar alterações'
						: 'Criar disponibilidade'}
				</button>
			</div>
		</div>
	);
};

export { CreateCalendarContainer };
